import { Component, computed, inject, InjectionToken, OnInit, Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';

import { filter, map, switchMap, take, tap } from 'rxjs';

import { AuditLogsActions } from 'src/app/features/audit-logs';
import { NotificationType } from 'src/app/features/notifications';
import { ToastService } from 'src/app/features/notifications/services';
import { PackageUsersSelectors } from 'src/app/features/package-users';
import { SessionLinkService } from 'src/app/features/check-in/services/session-link.service';
import { SessionHandoffService } from 'src/app/features/session-handoff/services/session-handoff.service';
import { PackagesActions } from 'src/app/features/packages';
import { RootStoreState } from 'src/app/store';
import { windowToken } from 'src/app/window.token';

@Component({
  selector: 'app-remote-welcome-page',
  templateUrl: './remote-welcome-page.component.html',
  styleUrls: ['./remote-welcome-page.component.scss'],
  standalone: false,
  providers: [SessionLinkService],
})
export class RemoteWelcomePageComponent implements OnInit {
  isNotMobile: Signal<boolean> = computed(() => this.windowRef.innerWidth > 575.98);
  sessionLinkService = inject(SessionLinkService);
  isAlertButtonDisabled = true;
  type = 'information';
  signingUri = '';

  constructor() {
    this.store
      .select(PackageUsersSelectors.selectNonSigningAgentsNotCheckedIn)
      .pipe(
        filter((nonSigningAgentsNotCheckedIn) => nonSigningAgentsNotCheckedIn.length > 0),
        map((nonSigningAgentsNotCheckedIn) => nonSigningAgentsNotCheckedIn[0]),
        filter(
          (nonSigningAgent): nonSigningAgent is NonNullable<typeof nonSigningAgent> =>
            Boolean(nonSigningAgent && nonSigningAgent.packageUserGuid)
        ),
        take(1),
        switchMap((nonSigningAgent) =>
          this.sessionLinkService
            .getPackageUserGuidLink(String(nonSigningAgent.packageUserGuid).toUpperCase())
            .pipe(
              take(1),
              tap(() => {
                this.isAlertButtonDisabled = false;
              })
            )
        ),
        tap((response) => {
          this.signingUri = response;
        }),
        takeUntilDestroyed()
      )
      .subscribe();
  }

  readonly alertBannerButtonText = 'Copy Link';
  readonly alertBannerCopyLinkErrorMessage = 'Failed to copy link.';
  readonly alertBannerCopyLinkSuccessMessage = 'Link copied successfully!';
  readonly alertBannerHeader = 'Multiple Signers on this Closing';

  private readonly windowRef = inject(windowToken) as Window;
  private readonly store = inject(Store<RootStoreState.State>);
  private readonly toastService = inject(ToastService);
  private readonly sessionHandoffService = inject(SessionHandoffService);
  private readonly navigator: Navigator = inject(
    new InjectionToken<Navigator>('Navigator', {
      providedIn: 'root',
      factory: () => (typeof navigator !== 'undefined' ? navigator : ({} as Navigator)),
    })
  );

  isSigningAgent = this.store.selectSignal(PackageUsersSelectors.getIsSigningAgent);
  shouldShowSessionHandoff = this.sessionHandoffService.shouldShowSessionHandoffComponent;
  private readonly nonSigningAgentsNotCheckedIn = this.store.selectSignal(
    PackageUsersSelectors.selectNonSigningAgentsNotCheckedIn
  );
  hasOtherSigners = computed(() => this.nonSigningAgentsNotCheckedIn().length > 0);

  async ngOnInit(): Promise<void> {
    this.store.dispatch(AuditLogsActions.LandingPageViewed());
    this.store.dispatch(PackagesActions.BeginSession());
  }

  async copyLink(): Promise<void> {
    try {
      this.type = this.signingUri.length > 0 ? 'information' : 'error';
      await this.navigator.clipboard.writeText(this.signingUri);
    } catch (error) {
      this.type = 'error';
      console.info(error?.message || this.alertBannerCopyLinkErrorMessage);
    }

    const isInformational = this.type === 'information';
    this.toastService.showNotification({
      id: globalThis.crypto.randomUUID(),
      notificationType: isInformational ? NotificationType.Success : NotificationType.Error,
      text: isInformational
        ? this.alertBannerCopyLinkSuccessMessage
        : this.alertBannerCopyLinkErrorMessage,
    });
  }
}
